// src/utils/loadAllInBatches.js

/**
 * Loads a full paginated dataset in small batches so the UI can render the
 * first batch immediately while the rest keeps loading in the background,
 * instead of blocking on one request for the entire dataset.
 *
 * @param {Function} fetchPage - async (page, size) => Spring Page response ({ content, totalPages, totalElements })
 * @param {Object} options
 * @param {number} options.batchSize - records per request (default 25)
 * @param {Function} options.onBatch - (batch, meta) => void, called after each page loads
 * @param {Function} [options.shouldContinue] - () => boolean, checked before each request; return false to abort (e.g. on unmount)
 */
export async function loadAllInBatches(fetchPage, { batchSize = 25, onBatch, shouldContinue } = {}) {
    let page = 0;
    let totalPages = 1;
    let totalElements = 0;

    do {
        if (shouldContinue && !shouldContinue()) {
            return;
        }

        const result = await fetchPage(page, batchSize);
        const batch = result?.content ?? (Array.isArray(result) ? result : []);
        totalPages = result?.totalPages ?? 1;
        totalElements = result?.totalElements ?? batch.length;

        if (onBatch) {
            onBatch(batch, { page, totalPages, totalElements, isFirst: page === 0, isLast: page + 1 >= totalPages });
        }

        page += 1;
    } while (page < totalPages);
}
